import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type { SessionState } from '../api/session';
import { useAppApi } from './ApiProvider';

/**
 * Вход в приложение (ТЗ §5). Один `POST /api/session` на запуск — и никакой формы
 * регистрации: аккаунт заводится сам.
 *
 * Провайдер НЕ загораживает собой дерево и ничего не рисует поверх экранов. Это осознанный
 * выбор: витрина — каталог, страница организации, сетка слотов — открыта без подписи, и
 * клиент, пришедший по QR-наклейке, обязан увидеть заведение сразу. Глобальный сплэш
 * «входим…» превратил бы вход, который вообще может не понадобиться, в задержку на пути
 * к главному экрану продукта.
 *
 * Ждать вход приходится только защищённым запросам, и ждут они его в транспорте
 * (`withSession` в `api/client.ts`), уже под собственным `Loader` того экрана, который
 * их отправил. Отдельного индикатора «заводится сессия» поэтому нет: на экране «Мои
 * записи» он был бы вторым спиннером над первым.
 */
export interface SessionValue {
  readonly state: SessionState;
  /**
   * Повторить вход. Ровно тот же вызов, что делает гейт защищённого запроса: две разные
   * входные точки со временем разошлись бы в условиях повтора.
   */
  retry(): void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider(props: { children: ReactNode }): JSX.Element {
  const api = useAppApi();

  /*
   * Состояние живёт в контроллере (`api.session`), а не в `useState`. Иначе сессия была бы
   * привязана к жизни компонента: StrictMode монтирует дерево дважды, и на каждом
   * монтировании уходил бы новый `POST /session`. Контроллер же переиспользует запрос
   * «в полёте» и уже готовый ответ — один вход на запуск, сколько бы раз ни перерисовалось
   * дерево.
   */
  const state = useSyncExternalStore(
    api.session.subscribe,
    api.session.getState,
    api.session.getState,
  );

  useEffect(() => {
    // Отказ здесь гасится намеренно: он уже лёг в состояние контроллера и будет показан
    // (или осознанно не показан — см. `unsigned` в `api/session.ts`). Проброс наружу дал бы
    // unhandledrejection в консоли на обычном открытии в браузере.
    void api.session.ensure().catch(() => undefined);
  }, [api]);

  const value = useMemo<SessionValue>(
    () => ({
      state,
      retry: () => {
        void api.session.ensure().catch(() => undefined);
      },
    }),
    [api, state],
  );

  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}

/**
 * Состояние входа для экранов.
 *
 * Экрану оно нужно РЕДКО: защищённый запрос и так ждёт сессию и падает с её причиной,
 * поэтому обычный экран ничего про сессию знать не обязан. Хук пригодится там, где кнопку
 * хочется погасить заранее (`state.status === 'ready'`) или объяснить, почему действие
 * недоступно вне Telegram (`'unsigned'`).
 */
export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  // Не подставляем «сессии нет»: компонент вне провайдера — дефект сборки дерева, и тихая
  // работа с выдуманным состоянием спрятала бы его до релиза (ср. `useAppApi`, `useI18n`).
  if (!value) throw new Error('useSession вызван вне <SessionProvider>');
  return value;
}
